import type { ReactNode } from "react"
import { useMemo } from "react"
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { IconGripDotsVerticalOutline18 as GripVertical } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import type {
	RoadmapDragRow,
	RoadmapRowType,
} from "@/screens/app/roadmap/reorder"
import { sortableRowId } from "@/screens/app/roadmap/reorder"

export const GRIP_CLASS =
	"flex size-4 flex-none cursor-default touch-none items-center justify-center rounded text-gray-400 opacity-0 hover:text-gray-600 focus-visible:opacity-100 dark:text-gray-500 dark:hover:text-gray-300"

export const HELD_CLASS =
	"bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10"

export function GripGlyph() {
	return <GripVertical className="size-3.5 flex-none transform-gpu" />
}

export function SortableRows({
	listId,
	itemIds,
	children,
}: {
	listId: string | null
	itemIds: readonly string[]
	children: ReactNode
}) {
	const ids = useMemo(
		() => (listId ? itemIds.map((id) => sortableRowId(listId, id)) : []),
		[listId, itemIds],
	)
	if (!listId) return <>{children}</>
	return (
		<SortableContext items={ids} strategy={verticalListSortingStrategy}>
			{children}
		</SortableContext>
	)
}

export function SortableListItem({
	listId,
	rowType,
	itemId,
	children,
}: {
	listId: string
	rowType: RoadmapRowType
	itemId: string
	children: (dragHandle: ReactNode) => ReactNode
}) {
	const data = useMemo<RoadmapDragRow>(
		() => ({ kind: "row", listId, rowType, itemId }),
		[listId, rowType, itemId],
	)
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: sortableRowId(listId, itemId), data })

	return (
		<div
			ref={setNodeRef}
			className={cn(isDragging && ["relative z-30 rounded-md", HELD_CLASS])}
			style={{
				// Translate only: a scale distorts rows of different heights mid-drag.
				transform: CSS.Translate.toString(transform),
				transition,
			}}
		>
			{children(
				<button
					type="button"
					ref={setActivatorNodeRef}
					aria-label="Drag to reorder"
					className={cn(
						GRIP_CLASS,
						"absolute left-0.5 top-1/2 -translate-y-1/2 group-hover:opacity-100",
						isDragging && "opacity-100",
					)}
					{...attributes}
					{...listeners}
					onClick={(event) => {
						event.preventDefault()
						event.stopPropagation()
					}}
				>
					<GripGlyph />
				</button>,
			)}
		</div>
	)
}
